// src/components/charts/WidgetChart.tsx
import React from "react";
import type { ChartWidget } from "../../api/types";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

type Props = {
  widget: ChartWidget;
  height?: number;
  // opzionale: per i bar
  mode?: "stacked" | "grouped";
};

// palette semplice (senza dipendere da theme)
const COLORS = ["#0d6efd", "#198754", "#ffc107", "#dc3545", "#6f42c1", "#20c997"];

function safeKey(label: string | null | undefined, idx: number) {
  const k = (label ?? "").trim();
  return k.length ? k : `series_${idx}`;
}

function formatTick(v: any) {
  const s = String(v ?? "");
  // se è ISO, mostra solo HH:mm (più leggibile)
  if (s.includes("T")) return s.slice(11, 16);
  return s.length > 12 ? s.slice(0, 12) + "…" : s;
}

function buildRows(widget: ChartWidget) {
  const keys = widget.datasets.map((ds, i) => safeKey(ds.label, i));

  const rows = widget.labels.map((lbl, idx) => {
    const row: any = { x: lbl };
    widget.datasets.forEach((ds, dsi) => {
      row[keys[dsi]] = Number(ds.data?.[idx] ?? 0);
    });
    return row;
  });

  return { rows, seriesKeys: keys };
}

export function WidgetChart({ widget, height = 280, mode }: Props) {
  const { rows, seriesKeys } = buildRows(widget);

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold">{widget.id}</div>
        <div className="text-muted small">{widget.unit ?? ""}</div>
      </div>

      <div style={{ width: "100%", minWidth: 1, height, minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200}>
          {widget.type === "line" ? (
            <LineChart data={rows}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="x" tick={{ fontSize: 12 }} tickFormatter={formatTick} />
              <YAxis tick={{ fontSize: 12 }} unit={widget.unit ?? undefined} />
              <Tooltip />
              <Legend />
              {seriesKeys.map((k, i) => (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={k}
                  name={widget.datasets[i]?.label ?? k}
                  dot={false}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          ) : widget.type === "bar" ? (
            <BarChart data={rows}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="x" tick={{ fontSize: 12 }} tickFormatter={formatTick} />
              <YAxis tick={{ fontSize: 12 }} unit={widget.unit ?? undefined} />
              <Tooltip />
              <Legend />
              {seriesKeys.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={k}
                  name={widget.datasets[i]?.label ?? k}
                  stackId={mode === "stacked" ? "stack" : undefined}
                  fill={COLORS[i % COLORS.length]}
                />
              ))}
            </BarChart>
          ) : (
            <PieChart>
              <Tooltip />
              <Legend />
              <Pie
                data={widget.labels.map((name, i) => ({
                  name,
                  value: Number(widget.datasets?.[0]?.data?.[i] ?? 0),
                }))}
                dataKey="value"
                nameKey="name"
                innerRadius="55%"
                outerRadius="80%"
                paddingAngle={2}
              >
                {widget.labels.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}